/* Shrinks an image in the browser before it goes to Firebase Storage.
 *
 * The JS team exports hero images straight out of slide decks, which land as
 * 1920x1080 PNGs of several MB. Uploading those raw would make the checkout
 * page's largest element its slowest one. Compressing here rather than with a
 * Storage extension keeps it instant, free, and visible to whoever's uploading
 * — they see the result before they publish.
 */

const DEFAULTS = { maxWidth: 1920, quality: 0.82 };

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }
  // Safari < 15 and friends.
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    img.src = url;
  });
}

function toBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function compressImage(file, options = {}) {
  const { maxWidth, quality } = { ...DEFAULTS, ...options };

  if (!file || !file.type || !file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }

  const bitmap = await loadBitmap(file);
  const srcW = bitmap.width;
  const srcH = bitmap.height;

  // Only ever scale down — upscaling a small image just wastes bytes.
  const scale = Math.min(1, maxWidth / srcW);
  const width = Math.round(srcW * scale);
  const height = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, width, height);
  if (bitmap.close) bitmap.close();

  let blob = await toBlob(canvas, "image/webp", quality);
  let type = "image/webp";

  // Older Safari silently ignores the webp request and hands back a PNG.
  if (!blob || blob.type !== "image/webp") {
    blob = await toBlob(canvas, "image/jpeg", quality);
    type = "image/jpeg";
  }
  if (!blob) throw new Error("Could not compress that image.");

  // An already-optimised upload can come out bigger after a re-encode.
  if (blob.size >= file.size) {
    return {
      blob: file,
      type: file.type,
      width: srcW,
      height: srcH,
      bytes: file.size,
      originalBytes: file.size,
      compressed: false,
    };
  }

  return {
    blob,
    type,
    width,
    height,
    bytes: blob.size,
    originalBytes: file.size,
    compressed: true,
  };
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
